import { LocalDate } from 'js-joda';
import { getEndOfMonth } from 'utils/timeUtils';

export type QuickFilterSelectionType =
  | 'upcoming'
  | 'today'
  | 'week'
  | 'weekend'
  | 'month'
  | 'nextmonth';

export const quickFilterDisplayValueForValue: { [key: string]: string } = {
  upcoming: 'All upcoming',
  today: 'Today',
  week: 'This week',
  weekend: 'This weekend',
  month: 'This month',
  nextmonth: 'Next month',
};

export function getQuickFilterLabel(filterType: QuickFilterSelectionType) {
  return quickFilterDisplayValueForValue[filterType as string];
}

const dateRangeResolver = new Map<
  QuickFilterSelectionType,
  () => { newStartDate: LocalDate; newEndDate: LocalDate }
>();

export function getDateRangeResolver() {
  const getOneYearDateRange = () => {
    const now = LocalDate.now();
    const endDate = now.plusYears(1);
    return { newStartDate: now, newEndDate: endDate };
  };

  const getThisWeekDateRange = () => {
    const now = LocalDate.now();
    const dayOfTheWeek = now.dayOfWeek().value(); //1 based. Monday is day 1
    const endDate = now.plusDays(7 - dayOfTheWeek);
    return { newStartDate: now, newEndDate: endDate };
  };

  const getThisWeekendDateRange = () => {
    const now = LocalDate.now();
    const dayOfTheWeek = now.dayOfWeek().value(); //1 based. Monday is day 1
    const weekendStartDate = dayOfTheWeek <= 5 ? now.plusDays(5 - dayOfTheWeek) : now;
    const weekEndDate = now.plusDays(7 - dayOfTheWeek);
    return { newStartDate: weekendStartDate, newEndDate: weekEndDate };
  };

  const getThisMonthDateRange = () => {
    const now = LocalDate.now();
    const endOfMonth = getEndOfMonth(now.month().value() - 1, now.year());
    const newEndDate = LocalDate.of(now.year(), now.month(), endOfMonth);
    return { newStartDate: now, newEndDate };
  };

  const getNextMonthDateRange = () => {
    const now = LocalDate.now();
    const endOfMonth = getEndOfMonth(now.month().value() - 1, now.year()); //get end of this month. value is 1 based
    const newStartDate = LocalDate.of(now.year(), now.month().value(), endOfMonth).plusDays(1); //get next day, which is start of next month
    const nextMonthEndDate = getEndOfMonth(newStartDate.month().value() - 1, newStartDate.year()); //get last day of next month
    const newEndDate = LocalDate.of(
      newStartDate.year(),
      newStartDate.month().value(),
      nextMonthEndDate
    );
    return { newStartDate, newEndDate };
  };

  const getTodayDateRange = () => {
    const now = LocalDate.now();
    return { newStartDate: now, newEndDate: now };
  };

  dateRangeResolver.set('upcoming', getOneYearDateRange);
  dateRangeResolver.set('today', getTodayDateRange);
  dateRangeResolver.set('week', getThisWeekDateRange);
  dateRangeResolver.set('weekend', getThisWeekendDateRange);
  dateRangeResolver.set('month', getThisMonthDateRange);
  dateRangeResolver.set('nextmonth', getNextMonthDateRange);

  return dateRangeResolver;
}

export function getQuickFilterType(
  date?: string,
  dateEnd?: string
): QuickFilterSelectionType | undefined {
  for (const [k, v] of dateRangeResolver.entries()) {
    const { newStartDate, newEndDate } = v();
    if (
      date !== undefined &&
      newStartDate.compareTo(LocalDate.parse(date)) === 0 &&
      dateEnd !== undefined &&
      newEndDate.compareTo(LocalDate.parse(dateEnd)) === 0
    ) {
      return k;
    }
  }
  return undefined;
}
